import { createContext, useState, useCallback, useContext, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { UserProfile } from '@spi/shared';
import { UserRole } from '@spi/shared';
import { apiFetch, setImpersonateUserId } from '../api/client';

interface PersonaContextValue {
  /** The user being impersonated, or null if not impersonating */
  persona: UserProfile | null;
  /** Whether we're currently in persona mode */
  isImpersonating: boolean;
  /** Start impersonating a user by ID */
  impersonate: (userId: number) => Promise<void>;
  /** Exit persona mode */
  exitPersona: () => void;
}

const PersonaContext = createContext<PersonaContextValue | null>(null);

export function PersonaProvider({ children, realUser }: { children: ReactNode; realUser: UserProfile | null }) {
  const [persona, setPersona] = useState<UserProfile | null>(null);
  const queryClient = useQueryClient();

  const impersonate = useCallback(async (userId: number) => {
    // Set the header so the API call to fetch profile uses impersonation
    setImpersonateUserId(userId);
    try {
      const profile = await apiFetch<UserProfile>('/api/users/me');
      setPersona(profile);
      // Invalidate all queries so they refetch with the impersonated user context
      queryClient.invalidateQueries();
    } catch {
      setImpersonateUserId(null);
      throw new Error('Failed to impersonate user');
    }
  }, [queryClient]);

  const exitPersona = useCallback(() => {
    setImpersonateUserId(null);
    setPersona(null);
    queryClient.invalidateQueries();
  }, [queryClient]);

  // Only allow admins to use persona
  const isAdmin = realUser?.role === UserRole.ADMIN;

  return (
    <PersonaContext.Provider
      value={{
        persona: isAdmin ? persona : null,
        isImpersonating: isAdmin ? !!persona : false,
        impersonate: isAdmin ? impersonate : async () => {},
        exitPersona: isAdmin ? exitPersona : () => {},
      }}
    >
      {children}
    </PersonaContext.Provider>
  );
}

export function usePersona() {
  const context = useContext(PersonaContext);
  if (!context) {
    throw new Error('usePersona must be used within a PersonaProvider');
  }
  return context;
}
